import React from 'react';
import Modal from '../components/shell/Modal';
import useTranslation from '../hooks/useTranslation';

/**
 * Privacy policy dialog.
 *
 * Reuses the accessible Xi modal (focus trap, Escape/backdrop close, focus
 * restore) so the agreement flow keeps the same `show` / `onAgree` /
 * `onDecline` contract while gaining keyboard and screen-reader support.
 */
export default function PrivacyPolicy({ show, onAgree, onDecline }) {
  const { t } = useTranslation();

  return (
    <Modal
      open={Boolean(show)}
      onClose={onDecline}
      title={t('Privacy Policy')}
      description={t('Please review the following privacy agreement before using our services.')}
      size="wide"
      closeLabel={t('Close privacy policy')}
      footer={
        <>
          <button type="button" className="xi-btn" onClick={onDecline}>
            {t('Decline')}
          </button>
          <button type="button" className="xi-btn xi-btn--primary" onClick={onAgree}>
            {t('I Agree')}
          </button>
        </>
      }
    >
      <div className="xi-legal">
        <p>
          <strong>{t('Welcome to Neurolonic!')}</strong>
        </p>
        <p>
          {t(
            'At Neurolonic, we are deeply committed to protecting your privacy and ensuring the security of your data. Please review the following privacy agreement before using our services.'
          )}
        </p>

        <section>
          <h3>1. {t('Data Collection and Use')}</h3>
          <p>
            {t(
              'Your audio recordings, as well as any resulting emotion and mental state analyses, will be securely stored on a private, encrypted cloud.'
            )}
          </p>
          <p>
            {t(
              'These data are used exclusively for providing you with insights into your emotional and mental states.'
            )}
          </p>
        </section>

        <section>
          <h3>2. {t('Data Privacy')}</h3>
          <p>
            {t(
              'We do not share or sell your data to any third parties. All personal and audio data remain strictly confidential.'
            )}
          </p>
          <p>
            {t(
              'Only you have access to your data, and it will never be disclosed or accessible by any unauthorized personnel.'
            )}
          </p>
        </section>

        <section>
          <h3>3. {t('Professional Access After Launch')}</h3>
          <p>
            {t(
              'After the official product launch, only certified professionals within Neurolonic may review data, solely for support and quality improvement purposes, and only with your explicit consent.'
            )}
          </p>
        </section>

        <section>
          <h3>4. {t('Security Measures')}</h3>
          <p>
            {t(
              'We employ industry-standard encryption and privacy safeguards to protect your data. Our team continuously monitors and updates our security protocols to maintain the highest level of data protection.'
            )}
          </p>
        </section>

        <section>
          <h3>{t('Agreement')}</h3>
          <p>
            {t(
              'By signing up and using Neurolonic, you agree to this privacy policy and understand that your data will remain secure and confidential. Thank you for trusting Neurolonic with your journey.'
            )}
          </p>
        </section>
      </div>
    </Modal>
  );
}
